import type {
    InviteStatusResponse,
    StartInviteJoinResponse,
} from '@common/api/featureService';

export type StableInvitePageState =
    | { state: 'missing-token' }
    | { state: 'loading' }
    | { state: 'error'; message: string }
    | { state: 'invalid'; status?: InviteStatusResponse | null }
    | { state: 'ready'; status: InviteStatusResponse };

export const readStableInviteToken = (search: string = ''): string => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('token')?.trim()
        || searchParams.get('invite_token')?.trim()
        || '';
};

export const readStableInviteAutoJoin = (search: string = ''): boolean => {
    const searchParams = new URLSearchParams(search);
    const autoJoin = searchParams.get('auto_join')?.trim().toLowerCase();
    return autoJoin === '1' || autoJoin === 'true';
};

export const resolveStableInviteState = (
    token: string,
    inviteStatus: InviteStatusResponse | null,
    isLoading: boolean,
    error: string | null,
): StableInvitePageState => {
    if (!token) {
        return { state: 'missing-token' };
    }

    if (isLoading) {
        return { state: 'loading' };
    }

    if (error) {
        return { state: 'error', message: error };
    }

    if (!inviteStatus?.valid) {
        return { state: 'invalid', status: inviteStatus };
    }

    return { state: 'ready', status: inviteStatus };
};

export const getInviteJoinRedirect = (response: StartInviteJoinResponse): string => {
    const authUrl = response.authUrl?.trim();

    if (!authUrl) {
        throw new Error('Invitation login did not return an auth link.');
    }

    return authUrl;
};

const normalizeEmail = (email?: string | null) => (email || '').trim().toLowerCase();

export const resolveStableInviteJoinAction = (
    inviteStatus: Pick<InviteStatusResponse, 'invitedEmail'>,
    currentSessionEmail?: string | null,
): 'accept' | 'start-auth' | 'wrong-account' => {
    const invitedEmail = normalizeEmail(inviteStatus.invitedEmail);
    const sessionEmail = normalizeEmail(currentSessionEmail);

    if (!sessionEmail) {
        return 'start-auth';
    }

    if (invitedEmail && invitedEmail !== sessionEmail) {
        return 'wrong-account';
    }

    return 'accept';
};
